import React, { useRef } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { PlusFill } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Stack, Typography } from '@lyearn/ui';
import useDnd from '@/components/DragDrop/DropOverContainer/useDnd';
import Loader from '@/components/Loader';

import useCategoryActions from '../../hooks/useCategoryActions';
import useCategoryPermissions from '../../hooks/useCategoryPermissions';
import useMoveCategory from '../../hooks/useMoveCategory';
import { CategoryLayoutView, CategoryType } from '../../types';
import CategoryTree from '../CategoryTree';
import { listItemStyles } from './styles';
import type { DraggingItemType, OnDragContentArgs } from './types';

interface CategoryListProps {
  rootCategory?: CategoryType | null;
  categoriesTree: CategoryType[] | null;
  fetching?: boolean;
  layoutView: CategoryLayoutView;
  actions: ReturnType<typeof useCategoryActions>['actions'];
  state: ReturnType<typeof useCategoryActions>['state'];
  showAddButton?: boolean;
  readonly?: boolean;
}

const CategoryList = React.memo(
  ({
    categoriesTree,
    fetching,
    layoutView,
    actions,
    state,
    showAddButton = false,
    rootCategory,
    readonly,
  }: CategoryListProps) => {
    const { t } = useTranslation();
    const draggingItem = useRef<DraggingItemType | undefined>();
    const { fetching: isMoving, onMerge } = useMoveCategory();
    const { permissions } = useCategoryPermissions();

    const onContentDrop = React.useCallback(
      ({ orderInNewParent, newParentCategory }: OnDragContentArgs) => {
        if (!draggingItem.current || !rootCategory) {
          return;
        }

        onMerge({
          newParentCategory,
          rootCategory,
          category: draggingItem.current.category,
          orderInNewParent: orderInNewParent,
        });
      },
      [onMerge, rootCategory],
    );

    const dndTop = useDnd({
      onDropCallback: () => {
        onContentDrop({
          orderInNewParent: categoriesTree?.length || 0,
          newParentCategory: undefined,
        });
      },
    });

    if (fetching) return <Loader />;

    const { onAddCategory } = actions;

    return (
      <Box
        className={cn(listItemStyles, { '-ms-32': layoutView === 'edit' })}
        data-system-category-count={categoriesTree?.length}
        style={readonly ? { pointerEvents: 'none' } : {}}>
        <CategoryTree
          actions={actions}
          categoryTrees={categoriesTree}
          draggingItem={draggingItem}
          hierarchyPath={[]}
          isMoving={isMoving}
          layoutView={layoutView}
          level={0}
          state={state}
          onDrop={onContentDrop}
        />
        {!showAddButton || !permissions.canCreate ? null : (
          <Stack
            className={cn('item-container h-[5.8rem]', { 'ms-32': layoutView === 'edit' })}
            justifyContent="start">
            <Box
              className="border-container flex flex-1 cursor-pointer flex-row items-center text-link-internal ps-16 hover:text-link-internal-hover"
              onClick={() => onAddCategory()}>
              <Box className="flex h-24 w-24 items-center justify-center">
                <PlusFill className="h-20 w-20" />
              </Box>
              <Box
                className={cn('flex h-full flex-1 flex-row items-center py-16 ps-8', {
                  'border-t border-interactive-primary': dndTop.isDragOver,
                })}
                onDragLeave={dndTop.onDragLeave}
                onDragOver={dndTop.onDragOver}
                onDrop={dndTop.onDrop}>
                <Typography variant="body-short-01">
                  {t('Discover:Category.Admin.AddCategory')}
                </Typography>
              </Box>
            </Box>
          </Stack>
        )}
      </Box>
    );
  },
);

export default React.memo(CategoryList);
